/*
 * Copyright (c) 2026 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthSessionService } from '@irian-ro/auth-oidc';
import { EnvironmentService } from '@irian-ro/util-environment';
import { filter } from 'rxjs/operators';
import { Cookies } from 'typescript-cookie';
import { ContextService } from './context.service';

/**
 * Marketing-analytics flag cookie on the shared parent domain, so the marketing site can tell
 * existing customers from prospects. Carries no user data: `deliwell_customer=true`.
 *
 * SET when the logged-in context resolves to a real user (`ContextService.onLoggedIn()`), which
 * replays on every boot of an authenticated tab — the 30-day expiry therefore slides.
 *
 * CLEARED on the OAuth lib's `logout` event, observed through `AuthSessionService.sessionChanges$`
 * as an invalid snapshot. That event is emitted synchronously from inside `oauthService.logOut()`,
 * i.e. BEFORE the IdP redirect, in both `OidcService.logout()` branches. We deliberately do NOT
 * use `ContextService.onLoggedOut()` for this: `OidcService.logout()` never clears the logged-in
 * context before redirecting, so that stream does not fire in the logging-out tab — it only fires
 * on the NEXT boot through the context BehaviorSubject's empty initial value.
 *
 * Enabled only where `marketingAnalyticsCookieDomain` is configured in the environment (production).
 */
@Injectable({ providedIn: 'root' })
export class MarketingAnalyticsCookieService {
  static readonly COOKIE_NAME = 'deliwell_customer';
  private static readonly EXPIRES_DAYS = 30;

  private readonly context = inject(ContextService);
  private readonly authSession = inject(AuthSessionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cookieDomain: string | null =
    inject(EnvironmentService).getEnvironmentProperty('marketingAnalyticsCookieDomain') ?? null;

  init(): void {
    const domain = this.cookieDomain;
    if (!domain) {
      return;
    }

    this.context
      .onLoggedIn()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setCookie(domain));

    this.authSession.sessionChanges$
      .pipe(
        filter((snapshot) => !snapshot.isValid),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.clearCookie(domain));
  }

  private setCookie(domain: string): void {
    Cookies.set(MarketingAnalyticsCookieService.COOKIE_NAME, 'true', {
      domain,
      path: '/',
      sameSite: 'Lax',
      secure: true,
      expires: MarketingAnalyticsCookieService.EXPIRES_DAYS,
    });
  }

  private clearCookie(domain: string): void {
    Cookies.remove(MarketingAnalyticsCookieService.COOKIE_NAME, { domain, path: '/' });
  }
}
